import React, {FC, useCallback, useMemo, useRef, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {Selection} from 'react-stately';
import clsx from 'clsx';
import Button from 'components/Button';
import Overlay from 'components/Layout/Overlay';
import Table from 'components/Table/TableFunctional';
import TooltipWrapper from 'components/Tooltip';
import TooltipIcon from 'components/Tooltip/TooltipIcon';
import Truncator from 'components/Truncator';
import {TruncatorProps} from 'components/Truncator/types';
import useOutsideDetector from 'hooks/useOutsideDetector';
import {useBackend} from 'state/backend';
import {Backend} from 'types/api';
import {onApiKeyError} from 'utils/request/result';
import ClientInboxDropdown from '../components/ClientInboxDropdown';
import FilterForm from '../components/FilterForm';
import DocumentInformation from '../components/Inbox/DocumentInformation';
import DownloadNotificationWarning from '../components/Inbox/DownloadNotificationWarning';
import InboxSectionHeader from '../components/Inbox/InboxHeader';
import InboxLayout from '../components/Inbox/InboxLayout';
import InboxPagination from '../components/Inbox/InboxPagination';
import InboxSectionSubtitle from '../components/Inbox/InboxSubtitle';
import InboxTableLayout from '../components/Inbox/TableLayout';
import {
    DocumentCheckboxFormFields,
    PaginationData,
    PortalPermissions,
} from '../components/Inbox/types';
import {isDocumentType} from '../components/Inbox/utils';
import DownloadDocumentModal from '../DownloadDocumentModal';
import usePortalLongTask from '../hooks/usePortalLongTask';
import {
    ClientInboxFormFields,
    Document,
    usePostDownloadClientInboxItems,
} from '../services';

type ClientInboxBackendData = {
    clientName: string;
    clientUsers: string[];
    documents: Document[];
    email: string;
    pagination: PaginationData;
    partnerName: string;
    permissions: PortalPermissions;
    portalMid: string;
    sessionExpiration: number;
};

export type ClientInboxBackend = Backend<ClientInboxBackendData>;

const ClientInbox: FC = () => {
    const {
        data: {
            clientName,
            clientUsers,
            documents,
            email,
            pagination,
            partnerName,
            permissions,
            portalMid,
            sessionExpiration,
        },
        flags,
    } = useBackend<ClientInboxBackend>();
    const {t} = useTranslation('Portal');
    const methods = useForm<DocumentCheckboxFormFields>();
    const {mutateAsync} = usePostDownloadClientInboxItems();
    const [rows, setRows] = useState<Document[]>(
        documents.map((d) => ({
            ...d,
            isInfoOverlayOpen: false,
            selected: false,
        })),
    );
    const [isOpen, setIsOpen] = useState(false);
    const [infoOverlay, setInfoOverlay] = useState<{
        document: Document;
        element: HTMLElement;
    } | null>(null);

    const openInfoPanel = useCallback(
        (
            event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
            document: Document,
        ) => {
            setInfoOverlay({
                document,
                element: event.currentTarget as HTMLElement,
            });

            const newRows = [...rows];
            const rowIndex = newRows.findIndex(
                (row) => row.mid === document.mid,
            );

            if (rowIndex >= 0) {
                newRows[rowIndex] = {
                    ...newRows[rowIndex],
                    isInfoOverlayOpen: true,
                };
            }

            setRows(newRows);
        },
        [rows],
    );

    const closeInfoPanel = useCallback(() => {
        setInfoOverlay(null);
        setRows(rows.map((row) => ({...row, isInfoOverlayOpen: false})));
    }, [rows]);

    const refOverlayContent = useRef<HTMLDivElement>(null);
    useOutsideDetector([refOverlayContent], closeInfoPanel);

    const {
        downloadRef,
        longTask,
        longTaskApiError,
        onFinish,
        progressRef,
        setLongTask,
    } = usePortalLongTask(portalMid, () => {
        setIsOpen(false);
        onFinish();
    });

    const onClose = () => {
        setIsOpen(false);
        onFinish();
    };

    const isBulkDownloadDisabled = useMemo(
        () =>
            rows.filter((d) => d.selected).length <= 1 ||
            rows.some((d) => d.selected && d.isInaccessible),
        [rows],
    );

    const showZipWarning = rows.some(
        (document) => document.filename.slice(-3) === 'zip',
    );

    const onClickDownload = () => {
        setIsOpen(true);
        methods.clearErrors('root.downloadResult');

        const formData: ClientInboxFormFields = {
            clientinboxItems: rows
                .filter(
                    (document) => document.selected && !document.isInaccessible,
                )
                .map((document) => document.mid),
            selectAll: false, // Only ddl the current page of selected documents, not all pages.
        };
        mutateAsync({
            formData,
            portalMid,
        })
            .then((res) => {
                if (res) setLongTask(res.data);
            })
            .catch((error) => {
                onApiKeyError(methods, 'downloadResult')(error);
            });
    };

    const onSelectionChange = (selection: Selection) => {
        closeInfoPanel(); // Stop showing current overlay when selecting something else, by clicking anywhere in the Table

        const newRows = [...rows];

        if (selection.toString() === 'all') {
            setRows(
                newRows.map((document) => ({
                    ...document,
                    isInfoOverlayOpen: false,
                    selected: !document.isInaccessible,
                })),
            );
        } else {
            const mids = [...selection];
            setRows(
                newRows.map((document) => ({
                    ...document,
                    isInfoOverlayOpen: false,
                    selected: mids.includes(document.mid),
                })),
            );
        }
    };

    const getInfoMessage = useCallback(
        (document: Document) => {
            if (document.isInaccessible) {
                return t('inbox.inaccessible');
            }

            if (document.isCancelled) {
                return t('inbox.cancelled');
            }

            return t('inbox.seeDetails');
        },
        [t],
    );

    const url = `/portal/${portalMid}/client-inbox`;

    const renderDocumentNumber = useCallback(
        (document: Document) => {
            const truncatorProps: TruncatorProps = {
                showFilenameExtension: !isDocumentType(document),
                text:
                    (isDocumentType(document)
                        ? document.documentNumber
                        : document.filename) || '',
                truncateAt: isDocumentType(document) ? 'right' : 'middle',
            };

            if (document.isCancelled) {
                return (
                    <div className="flex max-w-full line-through">
                        <TooltipIcon
                            className="my-1 mr-2 text-gold-700"
                            icon="warning"
                            message={t('inbox.cancelled')}
                            size="base"
                        />
                        <a
                            className="flex-1 min-w-0"
                            href={`${url}/${document.mid}/?history`}
                        >
                            <Truncator {...truncatorProps} />
                        </a>
                    </div>
                );
            }

            if (document.isInaccessible) {
                return (
                    <div className="flex max-w-full text-grey-400 line-through">
                        <TooltipIcon
                            className="my-1 mr-2 text-gold-700"
                            icon="warning"
                            message={t('inbox.inaccessible')}
                            size="base"
                        />
                        <Truncator
                            className="flex-1 min-w-0"
                            {...truncatorProps}
                        />
                    </div>
                );
            }

            return (
                <a href={`${url}/${document.mid}/?history`}>
                    <Truncator {...truncatorProps} />
                </a>
            );
        },
        [t, url],
    );

    const disabledKeys = rows
        .filter((row) => row.isInaccessible)
        .map((row) => row.mid);

    return (
        <InboxLayout
            clientName={clientName}
            email={email}
            flags={flags}
            navigation="client-inbox"
            partnerName={partnerName}
            permissions={permissions}
            portalMid={portalMid}
            sessionExpiration={sessionExpiration}
        >
            <FormProvider {...methods}>
                <InboxSectionHeader
                    buttonOnclick={onClickDownload}
                    isButtonDisabled={isBulkDownloadDisabled}
                    subtitle={
                        <InboxSectionSubtitle
                            canEdit={
                                permissions.hasEditAccessToClientInboxUsers
                            }
                            clientUsers={clientUsers}
                            partnerName={partnerName}
                            portalMid={portalMid}
                        />
                    }
                    title={t('clientInbox.title')}
                />
                <FilterForm flags={flags} url={`${url}/`} />
                {showZipWarning && <DownloadNotificationWarning />}
                <InboxTableLayout>
                    <Table
                        aria-label="client-inbox"
                        classNameTable="table-fixed"
                        disabledKeys={disabledKeys}
                        onSelectionChange={onSelectionChange}
                        selectionMode={
                            documents.length > 0 ? 'multiple' : 'none'
                        }
                    >
                        <Table.Header>
                            <Table.Column className="w-[7rem]">
                                {t('inbox.received')}
                            </Table.Column>
                            <Table.Column className="w-[7.375rem]">
                                <div className="flex max-w-full items-center">
                                    <span>{t('inbox.documentType')}</span>
                                    <TooltipIcon
                                        className="ml-1"
                                        icon="info"
                                        message={t('inbox.documentTypeTooltip')}
                                        size="base"
                                    />
                                </div>
                            </Table.Column>
                            <Table.Column className="w-[18.5rem]">
                                {t('inbox.documentNumber')}
                            </Table.Column>
                            <Table.Column>
                                {t('inbox.projectName')}
                            </Table.Column>
                            <Table.Column className="w-[7.438rem] text-end">
                                {t('inbox.total')}
                            </Table.Column>
                            <Table.Column className="w-[9.5rem]">
                                {' '}
                            </Table.Column>
                        </Table.Header>
                        {rows.length > 0 ? (
                            <Table.Body items={rows}>
                                {(document) => (
                                    <Table.Row
                                        key={document.mid}
                                        aria-label={`document-row-${document.mid}`}
                                    >
                                        <Table.Cell
                                            className={clsx(
                                                document.isInaccessible &&
                                                    'text-grey-400',
                                            )}
                                            preventPointerEvents={true}
                                        >
                                            {new Date(document.dateCreated)
                                                .toLocaleString('ja-JP', {
                                                    day: '2-digit',
                                                    month: '2-digit',
                                                    year: 'numeric',
                                                })
                                                .replaceAll('/', '-')}
                                        </Table.Cell>
                                        <Table.Cell preventPointerEvents={true}>
                                            <Truncator
                                                className={clsx(
                                                    isDocumentType(document)
                                                        ? 'bg-blue-50 text-blue-800'
                                                        : 'bg-grey-200 text-grey-800',
                                                    'w-fit max-w-full rounded-full px-2 py-0.5 text-sm font-semibold',
                                                )}
                                                text={document.documentTypeName}
                                            />
                                        </Table.Cell>
                                        <Table.Cell
                                            colSpan={
                                                isDocumentType(document) ? 1 : 3
                                            }
                                            preventPointerEvents={true}
                                        >
                                            {renderDocumentNumber(document)}
                                        </Table.Cell>
                                        <Table.Cell
                                            className={clsx(
                                                !isDocumentType(document) &&
                                                    'hidden',
                                                document.isCancelled &&
                                                    'line-through',
                                                document.isInaccessible &&
                                                    'text-grey-400 line-through',
                                            )}
                                            colSpan={
                                                isDocumentType(document) ? 1 : 0
                                            }
                                            preventPointerEvents={true}
                                        >
                                            {isDocumentType(document) && (
                                                <Truncator
                                                    className="max-w-full"
                                                    text={
                                                        document.projectName ||
                                                        ''
                                                    }
                                                />
                                            )}
                                        </Table.Cell>
                                        <Table.Cell
                                            className={clsx(
                                                !isDocumentType(document) &&
                                                    'hidden',
                                            )}
                                            colSpan={
                                                isDocumentType(document) ? 1 : 0
                                            }
                                            preventPointerEvents={true}
                                        >
                                            {isDocumentType(document) && (
                                                <Truncator
                                                    className={clsx(
                                                        'float-right max-w-full',
                                                        document.isCancelled &&
                                                            'line-through',
                                                        document.isInaccessible &&
                                                            'text-grey-400 line-through',
                                                    )}
                                                    text={document.total}
                                                />
                                            )}
                                        </Table.Cell>
                                        <Table.Cell
                                            className="flex gap-1"
                                            preventPointerEvents={true}
                                        >
                                            <TooltipWrapper
                                                message={
                                                    document.isInaccessible
                                                        ? t(
                                                              'inbox.inaccessible',
                                                          )
                                                        : t('inbox.download')
                                                }
                                            >
                                                <Button
                                                    disabled={
                                                        document.isInaccessible
                                                    }
                                                    icon="download"
                                                    kind="tertiary"
                                                    onClick={() => {
                                                        const newWindow: Window | null =
                                                            window.open(
                                                                `${url}/file-download/${document.mid}/`,
                                                            );

                                                        if (newWindow) {
                                                            newWindow.opener =
                                                                null;
                                                        }
                                                    }}
                                                    rel="noopener noreferrer"
                                                    size="xs"
                                                />
                                            </TooltipWrapper>
                                            <TooltipWrapper
                                                message={getInfoMessage(
                                                    document,
                                                )}
                                            >
                                                <Button
                                                    aria-label="info"
                                                    aria-selected={
                                                        document.isInfoOverlayOpen
                                                    }
                                                    disabled={
                                                        document.isInaccessible
                                                    }
                                                    icon="info"
                                                    kind="tertiary"
                                                    onClick={(event) =>
                                                        openInfoPanel(
                                                            event,
                                                            document,
                                                        )
                                                    }
                                                    size="xs"
                                                />
                                            </TooltipWrapper>
                                            <ClientInboxDropdown
                                                disabled={
                                                    document.isInaccessible
                                                }
                                                document={document}
                                                portalMid={portalMid}
                                            />
                                        </Table.Cell>
                                    </Table.Row>
                                )}
                            </Table.Body>
                        ) : (
                            <Table.Body>
                                <Table.Row>
                                    <Table.Cell>
                                        <div className="w-[4rem] whitespace-nowrap text-grey-600">
                                            {t('inbox.noDocuments')}
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell>{null}</Table.Cell>
                                    <Table.Cell>{null}</Table.Cell>
                                    <Table.Cell>{null}</Table.Cell>
                                    <Table.Cell>{null}</Table.Cell>
                                    <Table.Cell>{null}</Table.Cell>
                                </Table.Row>
                            </Table.Body>
                        )}
                    </Table>
                </InboxTableLayout>
                <InboxPagination
                    currentPage={pagination.currentPage}
                    numPages={pagination.numPages}
                    url={url}
                />
                <DownloadDocumentModal
                    downloadRef={downloadRef}
                    isOpen={isOpen}
                    longTask={longTask}
                    longTaskApiError={longTaskApiError}
                    onClose={onClose}
                    progressRef={progressRef}
                    selectedDocuments={rows}
                />
            </FormProvider>
            {infoOverlay?.document && (
                <Overlay
                    element={infoOverlay.element}
                    layoutLayer="z-tooltip"
                    layoutMethod="left"
                >
                    <div
                        ref={refOverlayContent}
                        aria-label="info-overlay"
                        className="relative text-base pointer-events-auto bg-white p-4 min-w-[30rem] border-grey-200 rounded elevation-400"
                        role="dialog"
                    >
                        <Button
                            aria-label="close"
                            className="absolute top-0 right-0 m-2"
                            icon="close_small"
                            kind="tertiary"
                            onClick={closeInfoPanel}
                            size="xs"
                        />
                        <DocumentInformation
                            document={infoOverlay.document}
                            isZipFile={
                                infoOverlay.document.filename.slice(-3) ===
                                'zip'
                            }
                        />
                    </div>
                </Overlay>
            )}
        </InboxLayout>
    );
};

export default ClientInbox;
